import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import { isConnectedToCloud } from "../../../App";
import AgentFunctions from "../../API/AgentFunctions";
import Agents from "../../API/Agents";
import alertsHelper from "../../API/Alerts/alertsHelper";
import authorisationsHelper from "../../API/Authorisations/authorisationsHelper";
import channelsHelper from "../../API/Channels/channelsHelper";
import sipAPI from "../../API/Channels/sipAPI";
import execEnvsHelper from "../../API/ExecutionEnvironments/execEnvsHelper";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../Router";
import Utility, { createUniqueCode, generateRowId } from "../../Utility/index";
import colorsMappings from "../Common/colorsMappings";
import { useModels } from "../Common/baseModels";
import Chats from "../Chats/ChatsScreenContainer";
import { HomeModeContext } from "../Home/HomeModeContext";
import QaSettingsAI from "../Settings/Components/QA/QaSettingsAI";
import AgentBuilderSidebar from "./Components/AgentBuilderSidebar";
import semverHelper from "../../Utils/semverHelper";
import Constants from "../../Constants";

// Hash-routing vocabulary: a `#<token>` on the agent URL opens that tab/section
// inside the agent builder. e.g. `/workspaces/:ws/agents/:agentId#tools`.
const HASH_ROUTED_TABS = ["build", "run", "widget", "evaluate"];
const HASH_ROUTED_SECTIONS = [
  "profile",
  "instructions",
  "knowledge",
  "tools",
  "internet",
  "memory",
  "planning",
  "capabilities",
  "triggers",
  "notifications",
  "desktop",
  "images",
  "videos",
  "multilingual",
  "moderation",
  "analytics",
  "feedback",
  "channels",
  "sip",
  "hosting",
  "advanced",
  "access",
  "versions",
];

const DEFAULT_AGENT_VALUES = {
  label: "",
  interpolationString: "",
  functionContext: null,
  description: "",
  defaultAnswer: "",
  inhibitionPassage: "",
  temperature: 0.5,
  llmSeed: null,
  blender: true,
  largeLogo: null,
  voiceFileKey: null,
  icoUrl: null,
  subject: null,
  goals: "",
  disclaimer: null,
  quickQuestions: null,
  allowFeedback: false,
  promptBuffering: false,
  preventWidgetUsage: true,
  color: null,
  darkColor: null,
  logoBackground: null,
  darkLogoBackground: null,
  placeholderInputMessage: null,
  placeholderMessageOnLoading: null,
  messageOnLaunch: null,
  pertinencePassage: "",
  mode: "retriever",
  minRetrievalScore: 0.65,
  topK: 5,
  maxHistory: 20,
  maxTokens: 500,
  showAgentName: false,
  showWidgetControls: true,
  showToolCallsDetailsInWidget: false,
  hideReasoning: true,
  maxTopics: 2,
  topicEnhancer: false,
  allowedTopics: [],
  staticDocs: [],
  ragSearchStrategy: "parallel",
  forcedTheme: null,
  summarisation: false,
  compressor: false,
  docTopK: 2,
  recencyImportance: 0,
  maxRecencyIncrease: 0,
  recencyTopKIncreaseFactor: 3,
  keywordsWeightInRetrieval: 0,
  extendedOutput: false,
  actions: "",
  agentFunctions: [],
  hasFunctions: false,
  hasImages: false,
  allowImagesUpload: false,
  advancedImageGeneration: false,
  reRank: false,
  allowInternetSearch: false,
  allowImagesGeneration: false,
  allowVideosGeneration: false,
  videoGenerationModel: "mystica_video_sd",
  allow3dModelGeneration: false,
  allowDeepInternetSearch: false,
  internetSearchModes: ["search"],
  internetSearchLocation: "Australia",
  bannedDomains: [],
  maxUrlsForInternetSearch: 5,
  minDeepSearchPagesPercentage: 0.5,
  allowVideosUpload: false,
  allowIntrospection: false,
  allowAudiosUpload: false,
  allowDocsUpload: false,
  hasNER: false,
  showReferencesToInternalDocs: false,
  showCitations: false,
  agenticRAG: false,
  useInterleavedReasoning: false,
  interleavedPreset: "medium",
  advancedLanguageDetection: false,
  restrictedAccess: false,
  restrictedAccessUsers: null,
  plannerApproverEmails: null,
  keyWordsForKnowledgeBase: false,
  contextRelevancyRatio: 0.7,
  reasoningBudget: 2048,
  reasoningMode: "always",
  reasoningEffort: "medium",
  splashTitle: null,
  showSplashWhenEmpty: false,
  plainTextOutput: false,
  generateExtendedReport: false,
  hasModeration: false,
  customModerations: Constants.defaultModerations,
  agentFnParams: null,
  showTimeForResponse: false,
  dynamicPlaceholderMessageOnLoading: true,
  customCodeExecutionEnvironments: [],
  showDetectedLanguage: false,
  showRoutedModel: false,
  isMultiLanguageEnabled: true,
  icon: null,
  llmProvider: "tf",
  llmBaseModel: "sorcerer",
  agenticCodingModel: "sorcerer",
  functionCallingProvider: "tf",
  functionCallingModel: "sorcerer",
  adverserialProvider: "tf",
  adverserialModel: "sorcerer",
  agentDesktopVM: null,
  agentDesktopPortRange: 5050,
  maxImagesGenerated: 4,
  hasCode: false,
  agentsPool: null,
  agentDesktopCredentials: null,
  agentDesktopBrowser: true,
  allowExternalAPI: false,
  maxPlanningSteps: 10,
  maxBrowserSteps: 50,
  allowIpRotationViaProxy: false,
  allowProxyScraping: false,
  plannerExecutionAttempts: 3,
  plannerRequiresApprovalForPlanExecution: true,
  plannerEmailOnApprovalRequest: true,
  plannerEmailOnFailedPlan: false,
  plannerEmailOnCompletedPlan: false,
  plannerAutoAgentSpawn: false,
  plannerDeepThinking: false,
  plannerRetainsSpawnedAgents: false,
  allowReplanning: false,
  showCodeInExecution: true,
  planningInstructions: null,
  reviewInstructions: null,
  maxTotalToolCalls: 200,
  maxConsecutiveSameTool: 20,
  maxConsecutiveFailures: 10,
  maxSessionUoI: null,
  maxSessionTurns: null,
  maxSessionMessage: null,
  phoneNumberAsSender: null,
  channelCallbackDelay: 0,
  whatsappNumberAsSender: null,
  emailAsSender: null,
  communicationServices: null,
  sipPhoneNumber: null,
  enableInboundSip: false,
  enableOutboundSip: false,
  sipDispatchRuleId: null,
  allowAudiosGeneration: false,
  audiosGenerationTtsModel: null,
  audiosGenerationVoiceName: null,
  audiosGenerationSpeechMode: null,
  customToolingInstructions: "",
  dynamicModelSelectionInstructions: "",
  chainOfThoughts: false,
  hasEntityExtraction: false,
  allowEmailToAgent: false,
  forceNewThreadOnProgrammaticRequest: false,
  allowedEmails: null,
  visionModel: "sorcerer",
  browserModel: null,
  isLongTermMemoryEnabled: false,
  agentLongTermMemory: null,
  languageDetectionConfidence: 0.9,
  moderationMessage: null,
  analyticsInstructions: null,
  analyticsCustomFields: null,
  alertIDs: [],
  ragInstructions: null,
  allowAskQuestion: false,
  minChunksBeforeCompleteness: 0.5,
  ragCompletenessThreshold: 0.75,
  voiceName: null,
  sttModel: "whisper-large-v3-turbo",
  llmVoiceModel: "sorcerer",
  ttsModel: "orpheus-fast",
  defaultVoiceLanguage: "en-US",
  voiceBackgroundNoise: "office",
  voiceInstructions: null,
  voiceMinSilenceDuration: 1.0,
  voiceMinEndpointingDelay: 0.8,
  voiceMinInterruptionDuration: 0.6,
  enableVoiceIntermissions: false,
  enableJsonOutput: false,
  jsonOutputStructure: null,
  predictedPlanJson: null,
};

const AgentBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const agentId = params?.agentId;
  const isEditMode = !!agentId && agentId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const {
    getActiveWorkspace,
    getActiveTrainingLog,
    getPlanConfig,
    getWorkspaceUsers,
    getAgent,
    loadAgents,
    agentWidgetsAvailable,
  } = useWorkspaces();
  const { isLoading: isModelsLoading } = useModels();

  const [activeTab, setActiveTab] = useState("build");
  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [availableFunctionsLoaded, setAvailableFunctionsLoaded] = useState(
    false,
  );
  const [
    customCodeExecutionEnvironments,
    setCustomCodeExecutionEnvironments,
  ] = useState([]);
  const [
    customCodeExecutionEnvironmentsLoaded,
    setCustomCodeExecutionEnvironmentsLoaded,
  ] = useState(false);
  const [agentsChannels, setAgentsChannels] = useState([]);
  const [availableAlerts, setAvailableAlerts] = useState([]);
  const [agentDesktopCredentials, setAgentDesktopCredentials] = useState(null);
  const [isAsyncProcessInProgress, setIsAsyncProcessInProgress] = useState(
    false,
  );
  const [saveVersion, setSaveVersion] = useState(0);
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [baseAgents, setBaseAgents] = useState([]);
  const [isBaseAgentsLoading, setIsBaseAgentsLoading] = useState(true);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [agentTypeToCreate, setAgentTypeToCreate] = useState(null);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedIndex = useMemo(
    () => state?.agents?.findIndex((u) => u?.id === agentId) ?? -1,
    [state?.agents, agentId],
  );
  const selectedAgent = useMemo(
    () => state?.agents?.find((u) => u?.id === agentId),
    [state?.agents, agentId],
  );

  const isChatBot =
    (selectedAgent?.agentType !== "ContentCreator" &&
      selectedAgent?.agentType !== "BusinessAnalyst" &&
      !agentTypeToCreate) ||
    agentTypeToCreate === "Chatbot";
  const isBusinessAnalyst =
    selectedAgent?.agentType === "BusinessAnalyst" ||
    agentTypeToCreate === "BusinessAnalyst";
  const isContentCreator =
    selectedAgent?.agentType === "ContentCreator" ||
    agentTypeToCreate === "ContentCreator";

  const generateColourForSearchModes = (mode) => {
    if (mode === "search") {
      return currentTheme?.theme?.primary;
    } else if (mode === "news") {
      return currentTheme?.theme?.green;
    } else if (mode === "images") {
      return currentTheme?.theme?.orange;
    } else if (mode == "videos") {
      return currentTheme?.theme?.red;
    } else if (mode == "shopping") {
      return currentTheme?.theme?.purple;
    } else {
      return currentTheme?.theme?.primary;
    }
  };

  const enhanceAgents = (agentsPoolIds) => {
    if (!state?.agents || state?.agents?.length === 0) {
      return agentsPoolIds;
    }
    return agentsPoolIds
      ?.map((id) => {
        const _agent = getAgent(id);
        if (_agent)
          return {
            id: _agent["id"],
            label: _agent["label"],
            name: _agent["label"],
            value: _agent["id"],
            category: _agent["agentType"],
            backgroundColor: colorsMappings.mapBackgroundColorWithAgentTypeAndMode(
              _agent,
              currentTheme,
            ),
          };
        return null;
      })
      ?.filter((agent) => agent != null);
  };

  const loadAvailableFunctions = async () => {
    try {
      const result = await AgentFunctions.getAgentFunctionsByWorkspaceID({
        workspaceID: getActiveWorkspace()?.id,
      });
      let functions = result?.data?.functionsByWorkspace?.items;
      functions =
        functions
          ?.map((u) => {
            if (u?._deleted !== true) return { ...u };
          })
          ?.filter((u) => u != null) || [];
      setAvailableFunctions(functions);
      setAvailableFunctionsLoaded(true);
      return functions;
    } catch {
      setAvailableFunctionsLoaded(false);
      return [];
    }
  };
  const loadCustomEnvironments = async () => {
    try {
      const result = await execEnvsHelper.getCustomCodeExecutionEnvironments(
        getActiveWorkspace()?.id,
      );
      let functions =
        result?.data?.customCodeExecutionEnvironmentsByWorkspace?.items;
      functions =
        functions
          ?.map((u) => {
            if (u?._deleted !== true) return { ...u };
          })
          ?.filter((u) => u != null) || [];
      setCustomCodeExecutionEnvironments(functions);
      setCustomCodeExecutionEnvironmentsLoaded(true);
      return functions;
    } catch {
      setCustomCodeExecutionEnvironmentsLoaded(true);
      return [];
    }
  };
  const loadChannels = async () => {
    try {
      const result = await channelsHelper.getChannels(getActiveWorkspace()?.id);
      let functions = result?.data?.CommunicationServicesByWorkspace?.items;
      functions =
        functions
          ?.map((u) => {
            if (u?._deleted !== true) return { ...u };
          })
          ?.filter((u) => u != null) || [];
      setAgentsChannels(functions);
      return functions;
    } catch {
      return [];
    }
  };
  const loadCredentials = async () => {
    try {
      const result = await authorisationsHelper.getAuthorisations(
        getActiveWorkspace()?.id,
      );
      const functions =
        result?.data?.authorisationsByWorkspace?.items
          ?.map((u) => {
            if (u?._deleted !== true) return { ...u };
          })
          ?.filter((u) => u != null) || [];
      setAgentDesktopCredentials(functions);
      return functions;
    } catch (error) {
      console.log("error", error);
      return [];
    }
  };
  const loadAlerts = async () => {
    try {
      const alerts = await alertsHelper.getAlertsByWorkspace(
        getActiveWorkspace()?.id,
      );
      setAvailableAlerts(alerts || []);
      return alerts || [];
    } catch {
      setAvailableAlerts([]);
      return [];
    }
  };

  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadAvailableFunctions();
      loadCustomEnvironments();
      loadChannels();
      loadCredentials();
      loadAlerts();
    }
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    const fetchBaseAgents = async () => {
      try {
        setIsBaseAgentsLoading(true);
        const resultBaseAgents = await Agents.getBaseAgentsByWorkspaceID(
          "root",
        );
        setBaseAgents(resultBaseAgents?.data?.agentsByWorkspace?.items);
      } catch (error) {
        console.log("Error while loading agents", error);
      } finally {
        setIsBaseAgentsLoading(false);
      }
    };
    fetchBaseAgents();
  }, [getActiveWorkspace()?.id]);

  const prepareEditValues = async (item) => {
    const functions = await loadAvailableFunctions();
    const envs = await loadCustomEnvironments();
    const channels = await loadChannels();
    const credentials = await loadCredentials();
    const alerts = await loadAlerts();
    if (
      item?.bannedDomains != null &&
      Array.isArray(item?.bannedDomains) &&
      item?.bannedDomains.length > 0
    ) {
      item.bannedDomains = item.bannedDomains.join(",");
    } else if (item != null) {
      item.bannedDomains = "";
    }
    const enhancedItem = state?.agents?.find((u) => u.id === item.id);
    if (enhancedItem?.label == null) {
      history.replace(
        `/workspaces/${getActiveWorkspace()?.id}/settings/roles_settings`,
      );
      return;
    }
    const enhancedFunctions = enhancedItem?.agentFunctions?.map((item) => {
      const _item = functions?.find((u) => u.id === item);
      if (!_item) return;
      return {
        ..._item,
        label: _item?.name,
        value: _item?.id,
        backgroundColor: currentTheme?.theme?.primary,
      };
    });
    const _credentials =
      typeof enhancedItem?.agentDesktopCredentials === "string"
        ? JSON.parse(enhancedItem?.agentDesktopCredentials)
        : enhancedItem?.agentDesktopCredentials;
    const enhancedCredentials = _credentials?.map((item) => {
      const _item = credentials?.find((u) => u.id === item);
      if (!_item) return;
      return {
        ..._item,
        label: _item?.name,
        value: _item?.id,
        backgroundColor: currentTheme?.theme?.primary,
      };
    });
    const enhancedEnvs = enhancedItem?.customCodeExecutionEnvironments?.map(
      (item) => {
        const _item = envs?.find((u) => u.id === item);
        if (!_item) return;
        return {
          ..._item,
          label: _item?.name,
          value: _item?.id,
          backgroundColor: currentTheme?.theme?.primary,
        };
      },
    );
    const enhancedChannels = enhancedItem?.communicationServices?.map(
      (item) => {
        const _item = channels?.find((u) => u.id === item);
        if (!_item) return;
        return {
          ..._item,
          label: _item?.name,
          value: _item?.id,
          backgroundColor: currentTheme?.theme?.primary,
        };
      },
    );
    const enhancedRestricedUsers = enhancedItem?.restrictedAccessUsers?.map(
      (item) => {
        const _item = getWorkspaceUsers()?.find((u) => u.id === item);
        if (!_item) return;
        return {
          ..._item,
          label: _item?.username || _item?.user_email,
          value: _item?.id,
          backgroundColor: AppUtilities.stringToHslColor(
            _item?.username || _item?.user_email,
            71,
          ),
        };
      },
    );
    const enhancedApproverUsers = enhancedItem?.plannerApproverEmails?.map(
      (item) => {
        const _item = getWorkspaceUsers()?.find((u) => u.id === item);
        if (!_item) return;
        return {
          ..._item,
          label: _item?.username || _item?.user_email,
          value: _item?.id,
          backgroundColor: AppUtilities.stringToHslColor(
            _item?.username || _item?.user_email,
            71,
          ),
        };
      },
    );
    const enhancedAlerts = enhancedItem?.alertIDs?.map((item) => {
      const _item = alerts?.find((u) => u.id === item);
      if (!_item) return;
      return {
        ..._item,
        label: _item?.name,
        value: _item?.id,
        backgroundColor: currentTheme?.theme?.primary,
      };
    });
    setInitialMapValues({
      ...enhancedItem,
      allowedTopics:
        typeof enhancedItem?.allowedTopics === "string"
          ? JSON.parse(enhancedItem?.allowedTopics)
          : enhancedItem?.allowedTopics,
      staticDocs:
        typeof enhancedItem?.staticDocs === "string"
          ? JSON.parse(enhancedItem?.staticDocs)
          : enhancedItem?.staticDocs,
      agentFunctions: enhancedFunctions
        ?.filter((u) => u != null)
        ?.map((u) => {
          return {
            ...u,
            label: u?.name + " - " + Utility?.mapFunctionTypeToLabel(u),
            value: u?.id,
            backgroundColor: Utility?.mapFunctionTypeToColour(u, currentTheme),
          };
        }),
      functionContext:
        typeof enhancedItem?.functionContext === "string"
          ? JSON.parse(enhancedItem?.functionContext)
          : enhancedItem?.functionContext,
      agentDesktopCredentials: enhancedCredentials
        ?.filter((u) => u != null)
        ?.map((u) => {
          return {
            ...u,
            label: u?.name + " - " + Utility?.mapFunctionTypeToLabel("chat"),
            value: u?.id,
            backgroundColor: Utility?.mapFunctionTypeToColour(u, currentTheme),
          };
        }),
      agentsPool: enhancedItem?.agentsPool?.map((item) => {
        const _item = state?.agents?.find((u) => u.id === item);
        if (!_item) return;
        return {
          ..._item,
          label: _item?.label,
          value: _item?.id,
          backgroundColor: currentTheme?.theme?.primary,
        };
      }),
      customCodeExecutionEnvironments: enhancedEnvs
        ?.filter((u) => u != null)
        ?.map((u) => {
          return {
            ...u,
            label: u?.name + " - " + "code_exec",
            value: u?.id,
            backgroundColor: Utility?.mapFunctionTypeToColour(u, currentTheme),
          };
        }),
      communicationServices: enhancedChannels
        ?.filter((u) => u != null)
        ?.map((u) => {
          return {
            ...u,
            label: u?.name + " - " + u?.provider,
            value: u?.id,
            backgroundColor: Utility?.mapFunctionTypeToColour(u, currentTheme),
          };
        }),
      sipPhoneNumber: enhancedItem?.sipPhoneNumber,
      enableInboundSip: enhancedItem?.enableInboundSip || false,
      enableOutboundSip: enhancedItem?.enableOutboundSip || false,
      sipDispatchRuleId: enhancedItem?.sipDispatchRuleId,
      agentFnParams: enhancedItem?.agentFnParams
        ? JSON.parse(enhancedItem?.agentFnParams)
        : null,
      customModerations: enhancedItem?.customModerations
        ? JSON.parse(enhancedItem?.customModerations)
        : null,
      restrictedAccessUsers: enhancedRestricedUsers?.filter((u) => u != null),
      plannerApproverEmails: enhancedApproverUsers?.filter((u) => u != null),
      alertIDs: enhancedAlerts?.filter((u) => u != null),
      analyticsCustomFields: enhancedItem?.analyticsCustomFields
        ? typeof enhancedItem?.analyticsCustomFields === "string"
          ? JSON.parse(enhancedItem?.analyticsCustomFields)
          : enhancedItem?.analyticsCustomFields
        : null,
      isMultiLanguageEnabled: enhancedItem?.isMultiLanguageEnabled != false,
      plannerRequiresApprovalForPlanExecution:
        enhancedItem?.plannerRequiresApprovalForPlanExecution != false,
      allowReplanning: enhancedItem?.allowReplanning == true,
      showCodeInExecution: enhancedItem?.showCodeInExecution != false,
      recencyImportance: enhancedItem?.recencyImportance || 0,
      contextRelevancyRatio: enhancedItem?.contextRelevancyRatio || 0.7,
      reasoningBudget: enhancedItem?.reasoningBudget,
      reasoningMode: enhancedItem?.reasoningMode || "always",
      reasoningEffort: enhancedItem?.reasoningEffort || "medium",
      maxUrlsForInternetSearch: enhancedItem?.maxUrlsForInternetSearch ?? 5,
      minDeepSearchPagesPercentage:
        enhancedItem?.minDeepSearchPagesPercentage ?? 0.5,
      keywordsWeightInRetrieval: enhancedItem?.keywordsWeightInRetrieval || 0,
      maxImagesGenerated: enhancedItem?.maxImagesGenerated || 4,
      channelCallbackDelay: enhancedItem?.channelCallbackDelay ?? 0,
      maxPlanningSteps: enhancedItem?.maxPlanningSteps || 5,
      maxBrowserSteps: enhancedItem?.maxBrowserSteps || 20,
      allowIpRotationViaProxy: enhancedItem?.allowIpRotationViaProxy || false,
      allowProxyScraping: enhancedItem?.allowProxyScraping || false,
      plannerExecutionAttempts: enhancedItem?.plannerExecutionAttempts || 2,
      languageDetectionConfidence:
        enhancedItem?.languageDetectionConfidence || 0.9,
      internetSearchModes:
        enhancedItem?.internetSearchModes?.map((u) => ({
          label: u,
          value: u,
          backgroundColor: generateColourForSearchModes(u),
        })) || [],
      minChunksBeforeCompleteness:
        enhancedItem.minChunksBeforeCompleteness || 0.5,
      ragCompletenessThreshold: enhancedItem.ragCompletenessThreshold || 0.75,
      maxTotalToolCalls: enhancedItem.maxTotalToolCalls || 200,
      maxConsecutiveSameTool: enhancedItem.maxConsecutiveSameTool || 20,
      maxConsecutiveFailures: enhancedItem.maxConsecutiveFailures || 10,
      voiceMinSilenceDuration: enhancedItem.voiceMinSilenceDuration || 1.0,
      voiceMinEndpointingDelay: enhancedItem.voiceMinEndpointingDelay || 0.8,
      voiceMinInterruptionDuration:
        enhancedItem.voiceMinInterruptionDuration || 0.6,
      enableVoiceIntermissions: enhancedItem.enableVoiceIntermissions || false,
      enableJsonOutput: enhancedItem.enableJsonOutput || false,
      jsonOutputStructure: enhancedItem?.jsonOutputStructure
        ? typeof enhancedItem.jsonOutputStructure === "string"
          ? JSON.parse(enhancedItem.jsonOutputStructure)
          : enhancedItem.jsonOutputStructure
        : null,
      predictedPlanJson: enhancedItem?.predictedPlanJson
        ? typeof enhancedItem.predictedPlanJson === "string"
          ? JSON.parse(enhancedItem.predictedPlanJson)
          : enhancedItem.predictedPlanJson
        : null,
      llmBaseModel:
        typeof enhancedItem?.llmBaseModel === "string" &&
        enhancedItem?.llmBaseModel.trim() !== ""
          ? enhancedItem.llmBaseModel
          : "sorcerer",
      functionCallingModel:
        typeof enhancedItem?.functionCallingModel === "string" &&
        enhancedItem?.functionCallingModel.trim() !== ""
          ? enhancedItem.functionCallingModel
          : "sorcerer",
      adverserialModel:
        typeof enhancedItem?.adverserialModel === "string" &&
        enhancedItem?.adverserialModel.trim() !== ""
          ? enhancedItem.adverserialModel
          : "sorcerer",
      visionModel:
        typeof enhancedItem?.visionModel === "string" &&
        enhancedItem?.visionModel.trim() !== ""
          ? enhancedItem.visionModel
          : "sorcerer",
      browserModel:
        typeof enhancedItem?.browserModel === "string" &&
        enhancedItem?.browserModel.trim() !== ""
          ? enhancedItem.browserModel
          : null,
      agenticCodingModel:
        typeof enhancedItem?.agenticCodingModel === "string" &&
        enhancedItem?.agenticCodingModel.trim() !== ""
          ? enhancedItem.agenticCodingModel
          : "sorcerer",
      llmVoiceModel:
        typeof enhancedItem?.llmVoiceModel === "string" &&
        enhancedItem?.llmVoiceModel.trim() !== ""
          ? enhancedItem.llmVoiceModel
          : "sorcerer",
    });
  };

  const prepareCreateValues = useCallback(
    (aiConfig = null) => {
      const baseAgentConfig = baseAgents?.find((u) => u?.mode == "retriever");
      const modelOrDefault = (value, fallback) =>
        typeof value === "string" && value.trim() !== "" ? value : fallback;
      const mergedConfig = aiConfig
        ? {
            ...baseAgentConfig,
            ...aiConfig,
            preventWidgetUsage: true,
            showWidgetControls: true,
            showToolCallsDetailsInWidget: false,
            minDeepSearchPagesPercentage:
              aiConfig?.minDeepSearchPagesPercentage ??
              baseAgentConfig?.minDeepSearchPagesPercentage ??
              0.5,
            maxUrlsForInternetSearch:
              aiConfig?.maxUrlsForInternetSearch ??
              baseAgentConfig?.maxUrlsForInternetSearch ??
              5,
            channelCallbackDelay:
              aiConfig?.channelCallbackDelay ??
              baseAgentConfig?.channelCallbackDelay ??
              0,
            llmBaseModel: modelOrDefault(
              aiConfig?.llmBaseModel,
              modelOrDefault(baseAgentConfig?.llmBaseModel, "sorcerer"),
            ),
            functionCallingModel: modelOrDefault(
              aiConfig?.functionCallingModel,
              modelOrDefault(baseAgentConfig?.functionCallingModel, "sorcerer"),
            ),
            adverserialModel: modelOrDefault(
              aiConfig?.adverserialModel,
              modelOrDefault(baseAgentConfig?.adverserialModel, "sorcerer"),
            ),
            visionModel: modelOrDefault(
              aiConfig?.visionModel,
              modelOrDefault(baseAgentConfig?.visionModel, "sorcerer"),
            ),
            agenticCodingModel: modelOrDefault(
              aiConfig?.agenticCodingModel,
              modelOrDefault(baseAgentConfig?.agenticCodingModel, "sorcerer"),
            ),
            llmVoiceModel: modelOrDefault(
              aiConfig?.llmVoiceModel,
              modelOrDefault(baseAgentConfig?.llmVoiceModel, "sorcerer"),
            ),
          }
        : {
            ...baseAgentConfig,
            preventWidgetUsage: true,
            showWidgetControls: true,
            showToolCallsDetailsInWidget: false,
            label: t("settings.list.newAgent"),
            mode: "retriever",
            minDeepSearchPagesPercentage:
              baseAgentConfig?.minDeepSearchPagesPercentage ?? 0.5,
            maxUrlsForInternetSearch:
              baseAgentConfig?.maxUrlsForInternetSearch ?? 5,
            channelCallbackDelay: baseAgentConfig?.channelCallbackDelay ?? 0,
            llmBaseModel: modelOrDefault(
              baseAgentConfig?.llmBaseModel,
              "sorcerer",
            ),
            functionCallingModel: modelOrDefault(
              baseAgentConfig?.functionCallingModel,
              "sorcerer",
            ),
            adverserialModel: modelOrDefault(
              baseAgentConfig?.adverserialModel,
              "sorcerer",
            ),
            visionModel: modelOrDefault(
              baseAgentConfig?.visionModel,
              "sorcerer",
            ),
            agenticCodingModel: modelOrDefault(
              baseAgentConfig?.agenticCodingModel,
              "sorcerer",
            ),
            llmVoiceModel: modelOrDefault(
              baseAgentConfig?.llmVoiceModel,
              "sorcerer",
            ),
          };
      setInitialMapValues(mergedConfig);
      setAgentTypeToCreate("Chatbot");
      setIsAIGenerated(!!aiConfig);
    },
    [baseAgents],
  );

  useEffect(() => {
    if (!agentId) {
      history.replace(`/workspaces/${workspaceId}/settings/roles_settings`);
    }
  }, [agentId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (agentId !== "new") return;
    if (createInitRef.current) return;
    if (isBaseAgentsLoading) return;
    createInitRef.current = true;
    const aiConfig = history.location?.state?.aiConfig || null;
    prepareCreateValues(aiConfig);
  }, [agentId, isEditMode, isBaseAgentsLoading, prepareCreateValues]);

  useEffect(() => {
    if (!isEditMode) return;
    if (!state?.agents || state?.agents?.length === 0) return;
    if (editInitRef.current === agentId) return;
    // Deep-link cold load: wait until the active workspace is resolved AND the
    // agents list actually belongs to it. Otherwise prepareEditValues loads
    // functions/channels/credentials against an undefined workspace and the
    // form initializes with empty data (e.g. the agent's tools go missing).
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const workspaceAgents = state?.agents?.filter(
      (u) => u?.workspaceID === activeWorkspaceId,
    );
    const agent = workspaceAgents?.find((u) => u?.id === agentId);
    if (!agent) {
      // The list still belongs to a previous workspace — keep waiting instead
      // of redirecting away from a valid deep link.
      const listIsStale = state?.agents?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId,
      );
      if (listIsStale) return;
      history.replace(
        `/workspaces/${activeWorkspaceId}/settings/roles_settings`,
      );
      return;
    }
    editInitRef.current = agentId;
    setAgentTypeToCreate(null);
    setIsAIGenerated(false);
    prepareEditValues(agent);
  }, [agentId, isEditMode, state?.agents, getActiveWorkspace()?.id]);

  const setupAgentValues = async () => {
    if (!initialMapValues) return;
    const authorisationsRaw = await AgentFunctions.getAgentFunctionsByWorkspaceID(
      {
        workspaceID: state?.activeWorkspaceID,
      },
    );
    let _auth = authorisationsRaw?.data?.functionsByWorkspace?.items;
    const authorisations =
      _auth?.filter((u) => u != null && u?._deleted != true) || [];
    const _enhancedCredentials = initialMapValues?.agentDesktopCredentials?.map(
      (cred) => {
        const _cred = authorisations?.find((u) => u?.id == cred);
        if (_cred)
          return {
            id: _cred["id"],
            label: _cred["label"],
            name: _cred["label"],
            value: _cred["id"],
            category: t("settings.list.desktopCredentials"),
            backgroundColor: colorsMappings.mapBackgroundColorWithAgentTypeAndMode(
              _cred,
              currentTheme,
            ),
          };
      },
    );
    setInitialMapValues({
      ...initialMapValues,
      qaUrl: getActiveTrainingLog("qa")?.id,
      recencyImportance: initialMapValues?.recencyImportance || 0,
      keywordsWeightInRetrieval:
        initialMapValues?.keywordsWeightInRetrieval || 0,
      channelCallbackDelay: initialMapValues?.channelCallbackDelay || 0,
      maxPlanningSteps: initialMapValues?.maxPlanningSteps || 10,
      maxBrowserSteps: initialMapValues?.maxBrowserSteps || 20,
      maxImagesGenerated:
        initialMapValues?.maxImagesGenerated > 0
          ? initialMapValues?.maxImagesGenerated
          : 4,
      plannerExecutionAttempts: initialMapValues?.plannerExecutionAttempts || 3,
      plannerRequiresApprovalForPlanExecution:
        initialMapValues?.plannerRequiresApprovalForPlanExecution != false,
      allowReplanning: initialMapValues?.allowReplanning == true,
      showCodeInExecution: initialMapValues?.showCodeInExecution != false,
      agentsPool: enhanceAgents(initialMapValues?.agentsPool),
      agentDesktopCredentials: Array.isArray(
        initialMapValues?.agentDesktopCredentials,
      )
        ? _enhancedCredentials
        : initialMapValues?.agentDesktopCredentials,
      contextRelevancyRatio:
        initialMapValues?.contextRelevancyRatio != null
          ? initialMapValues?.contextRelevancyRatio
          : 1,
      reasoningBudget: initialMapValues?.reasoningBudget || 2048,
      reasoningMode: initialMapValues?.reasoningMode,
      reasoningEffort: initialMapValues?.reasoningEffort,
      agentDesktopBrowser: initialMapValues?.agentDesktopBrowser,
      allowExternalAPI: initialMapValues?.allowExternalAPI != false,
      internetSearchModes: initialMapValues?.internetSearchModes?.map((u) =>
        typeof u === "string"
          ? {
              value: u,
              label: u,
              backgroundColor: generateColourForSearchModes(u),
            }
          : u,
      ),
      dynamicPlaceholderMessageOnLoading:
        initialMapValues?.dynamicPlaceholderMessageOnLoading || true,
      allowAskQuestion: initialMapValues?.allowAskQuestion || false,
      hideReasoning: initialMapValues?.hideReasoning != false,
      languageDetectionConfidence:
        initialMapValues?.languageDetectionConfidence || 0.9,
      minChunksBeforeCompleteness:
        initialMapValues?.minChunksBeforeCompleteness || 0.5,
      ragCompletenessThreshold:
        initialMapValues?.ragCompletenessThreshold || 0.75,
      voiceMinSilenceDuration: initialMapValues?.voiceMinSilenceDuration || 1.0,
      voiceMinEndpointingDelay:
        initialMapValues?.voiceMinEndpointingDelay || 0.8,
      voiceMinInterruptionDuration:
        initialMapValues?.voiceMinInterruptionDuration || 0.6,
      enableVoiceIntermissions:
        initialMapValues?.enableVoiceIntermissions || false,
      maxTotalToolCalls: initialMapValues?.maxTotalToolCalls || 200,
      maxConsecutiveSameTool: initialMapValues?.maxConsecutiveSameTool || 20,
      maxConsecutiveFailures: initialMapValues?.maxConsecutiveFailures || 10,
      maxSessionUoI: initialMapValues?.maxSessionUoI ?? null,
      maxSessionTurns: initialMapValues?.maxSessionTurns ?? null,
      maxSessionMessage: initialMapValues?.maxSessionMessage ?? null,
      enableJsonOutput: initialMapValues?.enableJsonOutput || false,
      jsonOutputStructure: initialMapValues?.jsonOutputStructure || null,
      predictedPlanJson: initialMapValues?.predictedPlanJson || null,
    });
  };

  useEffect(() => {
    if (agentTypeToCreate) setupAgentValues();
  }, [
    getActiveTrainingLog("qa")?.id,
    agentTypeToCreate,
    state?.workspaceUsers?.length,
    state?.agents?.length,
  ]);

  const isAgentsThreasholdReached = () => {
    return state?.agents?.length >= getPlanConfig()?.maxAgents;
  };

  const handleGenerationItemSave = async (values) => {
    const formValuesSnapshot = { ...values };
    if (
      values?.bannedDomains != null &&
      values?.bannedDomains != "" &&
      values?.bannedDomains.length > 0 &&
      typeof values?.bannedDomains === "string"
    ) {
      values.bannedDomains = values.bannedDomains.split(",");
      values.bannedDomains = values.bannedDomains
        .filter((u) => u)
        ?.map((u) => u.trim());
    } else {
      values.bannedDomains = values.bannedDomains || [];
    }
    if (values?.llmBaseModel == null) values.llmBaseModel = "sorcerer";
    if (values?.llmProvider == null) values.llmProvider = "tf";
    if (values?.functionCallingProvider == null)
      values.functionCallingProvider = "tf";
    if (values?.functionCallingModel == null)
      values.functionCallingModel = "sorcerer";
    if (values?.adverserialProvider == null) values.adverserialProvider = "tf";
    if (values?.adverserialModel == null) values.adverserialModel = "sorcerer";
    if (values?.qaUrl == "") values.qaUrl = null;
    values.agentsPool = values?.agentsPool?.map((u) => u?.id)?.filter((u) => u);
    values.agentDesktopCredentials = values?.agentDesktopCredentials?.map(
      (u) => u.id,
    );
    const hasAllowedTopics =
      values && values.allowedTopics && values.allowedTopics.length > 0;
    const isRetrieverChatbot =
      values &&
      ["retriever", "speed", "accuracy"].includes(values.mode) &&
      (values.agentType === "Chatbot" || values.agentType == null);
    const hasAgentFunctions =
      values && values.agentFunctions && values.agentFunctions.length > 0;
    if (
      !hasAllowedTopics &&
      ((isRetrieverChatbot &&
        !hasAgentFunctions &&
        !values?.allowDeepInternetSearch &&
        !values?.agenticRAG &&
        !values?.useInterleavedReasoning) ||
        values.agentType === "BusinessAnalyst")
    ) {
      AppToasty.show(
        values.agentType === "BusinessAnalyst"
          ? t("settings.list.topicSelectionRequired")
          : t("settings.list.topicFunctionOrInternetRequired"),
        {
          type: "danger",
        },
      );
      return;
    }
    if (
      values?.mode == "planner" &&
      (values?.agentsPool == null || values?.agentsPool?.length < 1) &&
      values?.plannerAutoAgentSpawn != true
    ) {
      AppToasty.show(t("settings.list.agentSelectionRequired"), {
        type: "danger",
      });
      return;
    }
    if (values?.preventWidgetUsage == false) {
      const numberOfAgentsWithWidget = await agentWidgetsAvailable(
        getActiveWorkspace()?.id,
        values?.id,
      );
      if (
        numberOfAgentsWithWidget >= 1 &&
        ["base", "pro"].includes(getActiveWorkspace()?.subscriptionType)
      ) {
        AppToasty.show(t("settings.list.widgetLimitReached"), {
          type: "danger",
        });
        return;
      }
    }

    if (isEditMode && (selectedIndex == null || selectedIndex < 0)) {
      AppToasty.show(t("qaCreationErrorCaption"), {
        type: "danger",
      });
      return;
    }

    try {
      setIsAsyncProcessInProgress(true);
      let creationResult;
      if (isEditMode && selectedIndex !== null && selectedIndex >= 0) {
        const editedRole = state?.agents[selectedIndex];

        if (editedRole) {
          editedRole.agentDesktopCredentials = JSON.stringify(
            values.agentDesktopCredentials,
          );
          editedRole.agentDesktopBrowser = values.agentDesktopBrowser;
          editedRole.allowExternalAPI = values.allowExternalAPI;
          editedRole.label = values.label;
          editedRole.interpolationString = values.interpolationString;
          editedRole.customToolingInstructions =
            values.customToolingInstructions;
          editedRole.dynamicModelSelectionInstructions =
            values.dynamicModelSelectionInstructions;
          editedRole.description = values.description;
          editedRole.defaultAnswer = values.defaultAnswer;
          editedRole.noKnowledgeDefaultAnswer = values.noKnowledgeDefaultAnswer;
          editedRole.inhibitionPassage = values.inhibitionPassage;
          editedRole.qaUrl = values.qaUrl;
          editedRole.temperature = values.temperature;
          editedRole.llmSeed = values.llmSeed;
          editedRole.blender = values.blender;
          editedRole.chainOfThoughts = values.chainOfThoughts;
          editedRole.hasEntityExtraction = values.hasEntityExtraction;
          editedRole.allowEmailToAgent = values.allowEmailToAgent;
          editedRole.forceNewThreadOnProgrammaticRequest =
            values.forceNewThreadOnProgrammaticRequest;
          editedRole.allowedEmails = values.allowedEmails;
          editedRole.largeLogo = values.largeLogo;
          editedRole.voiceFileKey = values.voiceFileKey;
          editedRole.icoUrl = values.icoUrl;
          editedRole.darkColor = values.darkColor;
          editedRole.logoBackground = values.logoBackground;
          editedRole.darkLogoBackground = values.darkLogoBackground;
          editedRole.goals = values.goals;
          editedRole.disclaimer = values.disclaimer;
          editedRole.quickQuestions = values.quickQuestions;
          editedRole.allowFeedback = values.allowFeedback;
          editedRole.promptBuffering = values.promptBuffering;
          editedRole.maxImagesGenerated = values.maxImagesGenerated;
          editedRole.baseImageGenerationModel = values.baseImageGenerationModel;
          editedRole.agentsPool = values.agentsPool;
          editedRole.maxPlanningSteps = values.maxPlanningSteps;
          editedRole.maxBrowserSteps = values.maxBrowserSteps;
          editedRole.allowIpRotationViaProxy = values.allowIpRotationViaProxy;
          editedRole.allowProxyScraping = values.allowProxyScraping;
          editedRole.plannerExecutionAttempts = values.plannerExecutionAttempts;
          editedRole.plannerRequiresApprovalForPlanExecution =
            values.plannerRequiresApprovalForPlanExecution;
          editedRole.plannerEmailOnApprovalRequest =
            values.plannerEmailOnApprovalRequest;
          editedRole.plannerEmailOnFailedPlan = values.plannerEmailOnFailedPlan;
          editedRole.plannerEmailOnCompletedPlan =
            values.plannerEmailOnCompletedPlan;
          editedRole.plannerAutoAgentSpawn = values.plannerAutoAgentSpawn;
          editedRole.plannerDeepThinking = values.plannerDeepThinking;
          editedRole.plannerRetainsSpawnedAgents =
            values.plannerRetainsSpawnedAgents;
          editedRole.allowReplanning = values.allowReplanning;
          editedRole.showCodeInExecution = values.showCodeInExecution;
          editedRole.planningInstructions = values.planningInstructions;
          editedRole.agentDesktopVM = values.agentDesktopVM;
          editedRole.agentDesktopPortRange = values.agentDesktopPortRange;
          editedRole.languageDetectionConfidence =
            values.languageDetectionConfidence;
          editedRole.moderationMessage = values.moderationMessage;
          editedRole.analyticsInstructions = values.analyticsInstructions;
          editedRole.analyticsCustomFields = values.analyticsCustomFields
            ? JSON.stringify(values.analyticsCustomFields)
            : null;
          editedRole.alertIDs =
            values.alertIDs?.map((u) => u?.id)?.filter((u) => u) || [];
          editedRole.ragInstructions = values.ragInstructions;
          editedRole.minChunksBeforeCompleteness =
            values.minChunksBeforeCompleteness || 0.5;
          editedRole.ragCompletenessThreshold =
            values.ragCompletenessThreshold || 0.75;
          editedRole.reviewInstructions = values.reviewInstructions;
          editedRole.maxTotalToolCalls = values.maxTotalToolCalls;
          editedRole.maxConsecutiveSameTool = values.maxConsecutiveSameTool;
          editedRole.maxConsecutiveFailures = values.maxConsecutiveFailures;
          editedRole.maxSessionUoI = values.maxSessionUoI;
          editedRole.maxSessionTurns = values.maxSessionTurns;
          editedRole.maxSessionMessage = values.maxSessionMessage;
          editedRole.preventWidgetUsage = values.preventWidgetUsage;
          editedRole.color = values.color;
          editedRole.placeholderInputMessage = values.placeholderInputMessage;
          editedRole.placeholderMessageOnLoading =
            values.placeholderMessageOnLoading;
          editedRole.messageOnLaunch = values.messageOnLaunch;
          editedRole.pertinencePassage = values.pertinencePassage;
          editedRole.subject = values.subject;
          editedRole.charting = values.charting;
          editedRole.hasTopicsContext = values.hasTopicsContext;
          editedRole.mode = values.mode;
          editedRole.topK = values.topK;
          editedRole.maxTokens = values.maxTokens;
          editedRole.showAgentName = values.showAgentName;
          editedRole.showWidgetControls = values.showWidgetControls;
          editedRole.showToolCallsDetailsInWidget =
            values.showToolCallsDetailsInWidget;
          editedRole.hideReasoning = values.hideReasoning;
          editedRole.maxTopics = values.maxTopics;
          editedRole.topicEnhancer = values.topicEnhancer;
          editedRole.allowedTopics = JSON.stringify(values.allowedTopics);
          editedRole.forcedTheme = values.forcedTheme;
          editedRole.staticDocs = JSON.stringify(values.staticDocs);
          editedRole.ragSearchStrategy = values.ragSearchStrategy;
          editedRole.functionContext = JSON.stringify(values.functionContext);
          editedRole.summarisation = values.summarisation;
          editedRole.agentLongTermMemory = values.agentLongTermMemory;
          editedRole.compressor = values.compressor;
          editedRole.icon = values.icon;
          editedRole.docTopK = values.docTopK;
          editedRole.llmProvider = values.llmProvider;
          editedRole.llmBaseModel = values.llmBaseModel;
          editedRole.regionalEndpoint = values.regionalEndpoint;
          editedRole.agenticCodingModel = values.agenticCodingModel;
          editedRole.functionCallingProvider = values.functionCallingProvider;
          editedRole.functionCallingModel = values.functionCallingModel;
          editedRole.adverserialProvider = values.adverserialProvider;
          editedRole.adverserialModel = values.adverserialModel;
          editedRole.visionModel = values.visionModel;
          editedRole.browserModel = values.browserModel;
          editedRole.voiceName = values.voiceName;
          editedRole.sttModel = values.sttModel;
          editedRole.llmVoiceModel = values.llmVoiceModel;
          editedRole.ttsModel = values.ttsModel;
          editedRole.defaultVoiceLanguage = values.defaultVoiceLanguage;
          editedRole.voiceBackgroundNoise = values.voiceBackgroundNoise;
          editedRole.voiceInstructions = values.voiceInstructions;
          editedRole.voiceMinSilenceDuration = values.voiceMinSilenceDuration;
          editedRole.voiceMinEndpointingDelay = values.voiceMinEndpointingDelay;
          editedRole.voiceMinInterruptionDuration =
            values.voiceMinInterruptionDuration;
          editedRole.enableVoiceIntermissions = values.enableVoiceIntermissions;
          editedRole.enableJsonOutput = values.enableJsonOutput;
          editedRole.jsonOutputStructure = JSON.stringify(
            values.jsonOutputStructure,
          );
          editedRole.predictedPlanJson = JSON.stringify(
            values.predictedPlanJson,
          );
          editedRole.recencyImportance = values.recencyImportance;
          editedRole.maxRecencyIncrease = values.maxRecencyIncrease;
          editedRole.recencyTopKIncreaseFactor =
            values.recencyTopKIncreaseFactor;
          editedRole.keywordsWeightInRetrieval =
            values.keywordsWeightInRetrieval || 0;
          editedRole.extendedOutput = values.extendedOutput;
          editedRole.maxHistory = values.maxHistory;
          editedRole.hasFunctions = values.hasFunctions;
          editedRole.hasImages = values.hasImages;
          editedRole.hasCode = values.hasCode;
          editedRole.allowImagesUpload = values.allowImagesUpload;
          editedRole.allowDeepInternetSearch = values.allowDeepInternetSearch;
          editedRole.internetSearchModes = values.internetSearchModes?.map(
            (u) => u?.value,
          );
          editedRole.internetSearchLocation = values.internetSearchLocation;
          editedRole.allowInternetSearch = values.allowDeepInternetSearch;
          editedRole.allowAskQuestion = values.allowAskQuestion ?? false;
          editedRole.allowImagesGeneration = values.allowImagesGeneration;
          editedRole.allowVideosGeneration = values.allowVideosGeneration;
          editedRole.videoGenerationModel = values.videoGenerationModel;
          editedRole.allow3dModelGeneration = values.allow3dModelGeneration;
          editedRole.advancedImageGeneration = values.advancedImageGeneration;
          editedRole.reRank = values.reRank;
          editedRole.bannedDomains = values.bannedDomains;
          editedRole.maxUrlsForInternetSearch =
            values.maxUrlsForInternetSearch || 5;
          editedRole.minDeepSearchPagesPercentage =
            values.minDeepSearchPagesPercentage || 0.5;
          editedRole.allowAudiosUpload = values.allowAudiosUpload;
          editedRole.allowDocsUpload = values.allowDocsUpload;
          editedRole.allowVideosUpload = values.allowVideosUpload;
          editedRole.allowIntrospection = values.allowIntrospection;
          editedRole.hasNER = values.hasNER;
          editedRole.showReferencesToInternalDocs =
            values.showReferencesToInternalDocs;
          editedRole.showCitations = values.showCitations;
          editedRole.agenticRAG = values.agenticRAG;
          editedRole.useInterleavedReasoning = values.useInterleavedReasoning;
          editedRole.interleavedPreset = values.interleavedPreset;
          editedRole.llmSeed = values.llmSeed;
          editedRole.contextRelevancyRatio = values.contextRelevancyRatio;
          editedRole.reasoningBudget = values.reasoningBudget;
          editedRole.reasoningMode = values.reasoningMode;
          editedRole.reasoningEffort = values.reasoningEffort;
          editedRole.splashTitle = values.splashTitle;
          editedRole.showSplashWhenEmpty = values.showSplashWhenEmpty;
          editedRole.keyWordsForKnowledgeBase = values.keyWordsForKnowledgeBase;
          editedRole.advancedLanguageDetection =
            values.advancedLanguageDetection;
          editedRole.agentFunctions = values.agentFunctions && [
            ...new Set(values.agentFunctions.map((item) => item.id)),
          ];
          editedRole.plainTextOutput = values.plainTextOutput;
          editedRole.generateExtendedReport = values.generateExtendedReport;
          editedRole.minRetrievalScore = values.minRetrievalScore;
          editedRole.hasModeration = values.hasModeration;
          editedRole.customModerations = values.customModerations
            ? typeof values.customModerations == "string"
              ? JSON.parse(values.customModerations)
              : values.customModerations
            : null;
          editedRole.agentFnParams = values.agentFnParams
            ? typeof values.agentFnParams == "string"
              ? JSON.parse(values.agentFnParams)
              : values.agentFnParams
            : null;
          editedRole.showTimeForResponse = values.showTimeForResponse;
          editedRole.dynamicPlaceholderMessageOnLoading =
            values.dynamicPlaceholderMessageOnLoading || true;
          editedRole.customCodeExecutionEnvironments = values.customCodeExecutionEnvironments && [
            ...new Set(
              values.customCodeExecutionEnvironments?.map((item) => item.id),
            ),
          ];
          editedRole.showDetectedLanguage = values.showDetectedLanguage;
          editedRole.showRoutedModel = values.showRoutedModel;
          editedRole.isMultiLanguageEnabled = values.isMultiLanguageEnabled;
          editedRole.restrictedAccess = values.restrictedAccess;
          editedRole.phoneNumberAsSender = values.phoneNumberAsSender;
          editedRole.channelCallbackDelay = values.channelCallbackDelay;
          editedRole.whatsappNumberAsSender = values.whatsappNumberAsSender;
          editedRole.emailAsSender = values.emailAsSender;
          editedRole.allowAudiosGeneration = values.allowAudiosGeneration;
          editedRole.audiosGenerationTtsModel =
            values.audiosGenerationTtsModel || null;
          editedRole.audiosGenerationVoiceName =
            values.audiosGenerationVoiceName || null;
          editedRole.audiosGenerationSpeechMode =
            values.audiosGenerationSpeechMode || null;
          editedRole.communicationServices = values.communicationServices?.map(
            (item) => item.id,
          );
          editedRole.sipPhoneNumber = values.sipPhoneNumber;
          editedRole.enableInboundSip = values.enableInboundSip;
          editedRole.enableOutboundSip = values.enableOutboundSip;
          editedRole.sipDispatchRuleId = values.sipDispatchRuleId;
          editedRole.restrictedAccessUsers = values.restrictedAccessUsers?.map(
            (item) => item.id,
          );
          editedRole.plannerApproverEmails = values.plannerApproverEmails?.map(
            (item) => item.id,
          );
          editedRole.jsonOutputStructure = values.jsonOutputStructure
            ? typeof values.jsonOutputStructure == "string"
              ? JSON.parse(values.jsonOutputStructure)
              : values.jsonOutputStructure
            : null;
          editedRole.predictedPlanJson = values.predictedPlanJson
            ? typeof values.predictedPlanJson == "string"
              ? JSON.parse(values.predictedPlanJson)
              : values.predictedPlanJson
            : null;
        }

        if (
          editedRole.isLongTermMemoryEnabled &&
          !values.isLongTermMemoryEnabled
        ) {
          const activeRoutineJobs = (state?.jobs || []).filter(
            (job) =>
              job.agentID === editedRole.id &&
              job.type === "ROUTINE" &&
              job.isActive &&
              !job._deleted,
          );
          if (activeRoutineJobs.length > 0) {
            const jobNames = activeRoutineJobs
              .map((j) => j.name || j.id)
              .join(", ");
            AppToasty.show(
              t("jobs.cannotDisableLTMWithActiveRoutines", {
                count: activeRoutineJobs.length,
                names: jobNames,
              }),
              { type: "danger", duration: 8000 },
            );
            setIsAsyncProcessInProgress(false);
            return;
          }
        }

        const editResultPart1 = await Agents.updateAgent(
          editedRole.id,
          [
            { property: "label", value: values.label },
            {
              property: "interpolationString",
              value: values.interpolationString,
            },
            {
              property: "customToolingInstructions",
              value: values.customToolingInstructions,
            },
            {
              property: "dynamicModelSelectionInstructions",
              value: values.dynamicModelSelectionInstructions,
            },
            { property: "description", value: values.description },
            { property: "defaultAnswer", value: values.defaultAnswer },
            {
              property: "noKnowledgeDefaultAnswer",
              value: values.noKnowledgeDefaultAnswer,
            },
            { property: "inhibitionPassage", value: values.inhibitionPassage },
            { property: "qaUrl", value: values.qaUrl },
            {
              property: "agentDesktopCredentials",
              value: JSON.stringify(values.agentDesktopCredentials),
            },
            {
              property: "agentDesktopBrowser",
              value: values.agentDesktopBrowser,
            },
            { property: "allowExternalAPI", value: values.allowExternalAPI },
            { property: "temperature", value: values.temperature },
            { property: "llmSeed", value: values.llmSeed },
            { property: "icon", value: values.icon },
            { property: "blender", value: values.blender },
            { property: "chainOfThoughts", value: values.chainOfThoughts },
            {
              property: "hasEntityExtraction",
              value: values.hasEntityExtraction,
            },
            { property: "allowEmailToAgent", value: values.allowEmailToAgent },
            {
              property: "forceNewThreadOnProgrammaticRequest",
              value: values.forceNewThreadOnProgrammaticRequest,
            },
            { property: "allowedEmails", value: values.allowedEmails },
            { property: "largeLogo", value: values.largeLogo },
            { property: "voiceFileKey", value: values.voiceFileKey },
            { property: "icoUrl", value: values.icoUrl },
            { property: "darkColor", value: values.darkColor },
            { property: "logoBackground", value: values.logoBackground },
            {
              property: "allowIpRotationViaProxy",
              value: values.allowIpRotationViaProxy,
            },
            {
              property: "allowProxyScraping",
              value: values.allowProxyScraping,
            },
            {
              property: "darkLogoBackground",
              value: values.darkLogoBackground,
            },
            { property: "goals", value: values.goals },
            { property: "disclaimer", value: values.disclaimer },
            { property: "quickQuestions", value: values.quickQuestions },
            { property: "allowFeedback", value: values.allowFeedback },
            { property: "promptBuffering", value: values.promptBuffering },
            { property: "agentsPool", value: values.agentsPool },
            { property: "maxPlanningSteps", value: values.maxPlanningSteps },
            { property: "maxBrowserSteps", value: values.maxBrowserSteps },
            {
              property: "plannerExecutionAttempts",
              value: values.plannerExecutionAttempts,
            },
            {
              property: "plannerRequiresApprovalForPlanExecution",
              value: values.plannerRequiresApprovalForPlanExecution,
            },
            { property: "allowReplanning", value: values.allowReplanning },
            {
              property: "showCodeInExecution",
              value: values.showCodeInExecution,
            },
            {
              property: "maxImagesGenerated",
              value: values.maxImagesGenerated,
            },
            {
              property: "baseImageGenerationModel",
              value: values.baseImageGenerationModel,
            },
            {
              property: "planningInstructions",
              value: values.planningInstructions,
            },
            { property: "moderationMessage", value: values.moderationMessage },
            {
              property: "analyticsInstructions",
              value: values.analyticsInstructions,
            },
            {
              property: "analyticsCustomFields",
              value: values.analyticsCustomFields
                ? JSON.stringify(values.analyticsCustomFields)
                : null,
            },
            {
              property: "alertIDs",
              value:
                values.alertIDs?.map((u) => u?.id)?.filter((u) => u) || [],
            },
            { property: "ragInstructions", value: values.ragInstructions },
            {
              property: "minChunksBeforeCompleteness",
              value: values.minChunksBeforeCompleteness,
            },
            {
              property: "ragCompletenessThreshold",
              value: values.ragCompletenessThreshold,
            },
            { property: "agentDesktopVM", value: values.agentDesktopVM },
            {
              property: "agentDesktopPortRange",
              value: values.agentDesktopPortRange,
            },
            {
              property: "plannerEmailOnApprovalRequest",
              value: values.plannerEmailOnApprovalRequest,
            },
            {
              property: "plannerEmailOnFailedPlan",
              value: values.plannerEmailOnFailedPlan,
            },
            {
              property: "plannerEmailOnCompletedPlan",
              value: values.plannerEmailOnCompletedPlan,
            },
            {
              property: "plannerAutoAgentSpawn",
              value: values.plannerAutoAgentSpawn,
            },
            {
              property: "plannerDeepThinking",
              value: values.plannerDeepThinking,
            },
            {
              property: "plannerRetainsSpawnedAgents",
              value: values.plannerRetainsSpawnedAgents,
            },
            {
              property: "reviewInstructions",
              value: values.reviewInstructions,
            },
            { property: "maxTotalToolCalls", value: values.maxTotalToolCalls },
            {
              property: "maxConsecutiveSameTool",
              value: values.maxConsecutiveSameTool,
            },
            {
              property: "maxConsecutiveFailures",
              value: values.maxConsecutiveFailures,
            },
            { property: "maxSessionUoI", value: values.maxSessionUoI },
            { property: "maxSessionTurns", value: values.maxSessionTurns },
            { property: "maxSessionMessage", value: values.maxSessionMessage },
            {
              property: "preventWidgetUsage",
              value: values.preventWidgetUsage,
            },
            { property: "color", value: values.color },
            {
              property: "isMultiLanguageEnabled",
              value: values.isMultiLanguageEnabled,
            },
            {
              property: "placeholderInputMessage",
              value: values.placeholderInputMessage,
            },
            {
              property: "placeholderMessageOnLoading",
              value: values.placeholderMessageOnLoading,
            },
            { property: "messageOnLaunch", value: values.messageOnLaunch },
            { property: "pertinencePassage", value: values.pertinencePassage },
            { property: "subject", value: values.subject },
            { property: "charting", value: values.charting },
            { property: "hasTopicsContext", value: values.hasTopicsContext },
            { property: "mode", value: values.mode },
            { property: "topK", value: values.topK },
            { property: "maxTokens", value: values.maxTokens },
            { property: "showAgentName", value: values.showAgentName },
            {
              property: "showWidgetControls",
              value: values.showWidgetControls,
            },
            {
              property: "showToolCallsDetailsInWidget",
              value: values.showToolCallsDetailsInWidget,
            },
            { property: "hideReasoning", value: values.hideReasoning },
            { property: "maxTopics", value: values.maxTopics },
            { property: "forcedTheme", value: values.forcedTheme },
            { property: "topicEnhancer", value: values.topicEnhancer },
            {
              property: "allowedTopics",
              value: JSON.stringify(values.allowedTopics),
            },
            {
              property: "staticDocs",
              value: JSON.stringify(values.staticDocs),
            },
            {
              property: "ragSearchStrategy",
              value: values.ragSearchStrategy,
            },
            {
              property: "functionContext",
              value: JSON.stringify(values.functionContext),
            },
            {
              property: "customModerations",
              value: values.customModerations
                ? typeof values.customModerations == "object"
                  ? JSON.stringify(values?.customModerations)
                  : values.customModerations
                : null,
            },
            {
              property: "agentFnParams",
              value: values.agentFnParams
                ? typeof values.agentFnParams == "object"
                  ? JSON.stringify(values?.agentFnParams)
                  : values.agentFnParams
                : null,
            },
            {
              property: "showTimeForResponse",
              value: values.showTimeForResponse,
            },
            {
              property: "dynamicPlaceholderMessageOnLoading",
              value: values.dynamicPlaceholderMessageOnLoading,
            },
            {
              property: "customCodeExecutionEnvironments",
              value: values.customCodeExecutionEnvironments?.map((u) => u?.id),
            },
            {
              property: "showDetectedLanguage",
              value: values.showDetectedLanguage,
            },
            { property: "showRoutedModel", value: values.showRoutedModel },
            {
              property: "communicationServices",
              value: values.communicationServices?.map((item) => item.id),
            },
            { property: "sipPhoneNumber", value: values.sipPhoneNumber },
            { property: "enableInboundSip", value: values.enableInboundSip },
            { property: "enableOutboundSip", value: values.enableOutboundSip },
            { property: "sipDispatchRuleId", value: values.sipDispatchRuleId },
            {
              property: "phoneNumberAsSender",
              value: values.phoneNumberAsSender,
            },
            {
              property: "channelCallbackDelay",
              value: values.channelCallbackDelay,
            },
            {
              property: "whatsappNumberAsSender",
              value: values.whatsappNumberAsSender,
            },
            { property: "emailAsSender", value: values.emailAsSender },
            {
              property: "isLongTermMemoryEnabled",
              value: values.isLongTermMemoryEnabled,
            },
            {
              property: "agentLongTermMemory",
              value: values.agentLongTermMemory,
            },
            {
              property: "agentCategoryType",
              value:
                values.agentCategoryType === "none" ||
                values.agentCategoryType === null
                  ? null
                  : values.agentCategoryType,
            },
            {
              property: "languageDetectionConfidence",
              value: values.languageDetectionConfidence,
            },
          ],
          user?.id,
        );
        const editResultPart2 = await Agents.updateAgent(
          editedRole.id,
          [
            { property: "summarisation", value: values.summarisation },
            {
              property: "allowAudiosGeneration",
              value: values.allowAudiosGeneration,
            },
            {
              property: "audiosGenerationTtsModel",
              value: values.audiosGenerationTtsModel || null,
            },
            {
              property: "audiosGenerationVoiceName",
              value: values.audiosGenerationVoiceName || null,
            },
            {
              property: "audiosGenerationSpeechMode",
              value: values.audiosGenerationSpeechMode || null,
            },
            { property: "compressor", value: values.compressor },
            { property: "docTopK", value: values.docTopK },
            { property: "llmProvider", value: values.llmProvider },
            { property: "llmBaseModel", value: values.llmBaseModel },
            { property: "regionalEndpoint", value: values.regionalEndpoint },
            {
              property: "agenticCodingModel",
              value: values.agenticCodingModel,
            },
            {
              property: "functionCallingProvider",
              value: values.functionCallingProvider,
            },
            {
              property: "functionCallingModel",
              value: values.functionCallingModel,
            },
            {
              property: "adverserialProvider",
              value: values.adverserialProvider,
            },
            { property: "adverserialModel", value: values.adverserialModel },
            { property: "visionModel", value: values.visionModel },
            { property: "browserModel", value: values.browserModel },
            { property: "voiceName", value: values.voiceName },
            { property: "sttModel", value: values.sttModel },
            { property: "llmVoiceModel", value: values.llmVoiceModel },
            { property: "ttsModel", value: values.ttsModel },
            {
              property: "defaultVoiceLanguage",
              value: values.defaultVoiceLanguage,
            },
            {
              property: "voiceBackgroundNoise",
              value: values.voiceBackgroundNoise,
            },
            { property: "voiceInstructions", value: values.voiceInstructions },
            {
              property: "voiceMinSilenceDuration",
              value: values.voiceMinSilenceDuration,
            },
            {
              property: "voiceMinEndpointingDelay",
              value: values.voiceMinEndpointingDelay,
            },
            {
              property: "voiceMinInterruptionDuration",
              value: values.voiceMinInterruptionDuration,
            },
            {
              property: "enableVoiceIntermissions",
              value: values.enableVoiceIntermissions,
            },
            { property: "enableJsonOutput", value: values.enableJsonOutput },
            {
              property: "jsonOutputStructure",
              value: values.jsonOutputStructure
                ? typeof values.jsonOutputStructure == "object"
                  ? JSON.stringify(values?.jsonOutputStructure)
                  : values.jsonOutputStructure
                : null,
            },
            {
              property: "predictedPlanJson",
              value: values.predictedPlanJson
                ? typeof values.predictedPlanJson == "object"
                  ? JSON.stringify(values?.predictedPlanJson)
                  : values.predictedPlanJson
                : null,
            },
            {
              property: "recencyImportance",
              value: values.recencyImportance,
            },
            {
              property: "maxRecencyIncrease",
              value: values.maxRecencyIncrease,
            },
            {
              property: "recencyTopKIncreaseFactor",
              value: values.recencyTopKIncreaseFactor,
            },
            {
              property: "keywordsWeightInRetrieval",
              value: values.keywordsWeightInRetrieval,
            },
            { property: "extendedOutput", value: values.extendedOutput },
            { property: "maxHistory", value: values.maxHistory },
            { property: "workspaceID", value: getActiveWorkspace()?.id },
            { property: "hasFunctions", value: values.hasFunctions },
            { property: "hasImages", value: values.hasImages },
            { property: "hasCode", value: values.hasCode },
            {
              property: "allowImagesUpload",
              value: values.allowImagesUpload,
            },
            {
              property: "allowInternetSearch",
              value: values.allowDeepInternetSearch,
            },
            {
              property: "allowAskQuestion",
              value: values.allowAskQuestion ?? false,
            },
            {
              property: "advancedImageGeneration",
              value: values.advancedImageGeneration,
            },
            { property: "reRank", value: values.reRank },
            {
              property: "allowImagesGeneration",
              value: values.allowImagesGeneration,
            },
            {
              property: "allowVideosGeneration",
              value: values.allowVideosGeneration,
            },
            {
              property: "videoGenerationModel",
              value: values.videoGenerationModel,
            },
            {
              property: "allow3dModelGeneration",
              value: values.allow3dModelGeneration,
            },
            {
              property: "allowDeepInternetSearch",
              value: values.allowDeepInternetSearch,
            },
            {
              property: "internetSearchLocation",
              value: values.internetSearchLocation,
            },
            {
              property: "internetSearchModes",
              value: values.internetSearchModes?.map((u) => u?.value),
            },
            { property: "bannedDomains", value: values.bannedDomains },
            {
              property: "minDeepSearchPagesPercentage",
              value: values.minDeepSearchPagesPercentage,
            },
            {
              property: "maxUrlsForInternetSearch",
              value: values.maxUrlsForInternetSearch,
            },
            {
              property: "allowAudiosUpload",
              value: values.allowAudiosUpload,
            },
            { property: "allowDocsUpload", value: values.allowDocsUpload },
            {
              property: "allowVideosUpload",
              value: values.allowVideosUpload,
            },
            {
              property: "allowIntrospection",
              value: values.allowIntrospection,
            },
            { property: "hasNER", value: values.hasNER },
            {
              property: "showReferencesToInternalDocs",
              value: values.showReferencesToInternalDocs,
            },
            { property: "showCitations", value: values.showCitations },
            { property: "agenticRAG", value: values.agenticRAG },
            {
              property: "useInterleavedReasoning",
              value: values.useInterleavedReasoning,
            },
            {
              property: "interleavedPreset",
              value: values.interleavedPreset,
            },
            { property: "llmSeed", value: values.llmSeed },
            {
              property: "advancedLanguageDetection",
              value: values.advancedLanguageDetection,
            },
            { property: "restrictedAccess", value: values.restrictedAccess },
            {
              property: "restrictedAccessUsers",
              value: values.restrictedAccessUsers?.map((item) => item.id),
            },
            {
              property: "plannerApproverEmails",
              value: values.plannerApproverEmails?.map((item) => item.id),
            },
            {
              property: "contextRelevancyRatio",
              value: values.contextRelevancyRatio,
            },
            { property: "reasoningBudget", value: values.reasoningBudget },
            { property: "reasoningMode", value: values.reasoningMode },
            { property: "reasoningEffort", value: values.reasoningEffort },
            { property: "splashTitle", value: values.splashTitle },
            {
              property: "showSplashWhenEmpty",
              value: values.showSplashWhenEmpty,
            },
            {
              property: "keyWordsForKnowledgeBase",
              value: values.keyWordsForKnowledgeBase,
            },
            {
              property: "agentFunctions",
              value: values.agentFunctions?.map((item) => item.id),
            },
            {
              property: "plainTextOutput",
              value: values.plainTextOutput,
            },
            {
              property: "generateExtendedReport",
              value: values.generateExtendedReport,
            },
            {
              property: "minRetrievalScore",
              value: values.minRetrievalScore,
            },
            { property: "hasModeration", value: values.hasModeration },
            {
              property: "agentVersion",
              value: semverHelper.normalizeVersion(values.agentVersion),
            },
          ],
          user?.id,
        );
        const newAgent = {
          ...editResultPart1.data?.updateAgent,
          ...editResultPart2?.data?.updateAgent,
        };
        const newAgentsArray = [...state?.agents];
        newAgentsArray[selectedIndex] = newAgent;
        dispatch("SET_AGENTS", newAgentsArray);
      } else if (!isEditMode) {
        if (isAgentsThreasholdReached()) {
          AppToasty.show(t("agentsThreasholdErrorCaption"), {
            type: "danger",
          });
          setIsAsyncProcessInProgress(false);
          return;
        }
        let newRole = {
          type: createUniqueCode(generateRowId()),
          label: values.label,
          temperature: parseFloat(values.temperature),
          blender: values.blender,
          chainOfThoughts: values.chainOfThoughts,
          hasEntityExtraction: values.hasEntityExtraction,
          allowEmailToAgent: values.allowEmailToAgent,
          forceNewThreadOnProgrammaticRequest:
            values.forceNewThreadOnProgrammaticRequest,
          allowedEmails: values.allowedEmails,
          largeLogo: values.largeLogo,
          voiceFileKey: values.voiceFileKey,
          icoUrl: values.icoUrl,
          darkColor: values.darkColor,
          logoBackground: values.logoBackground,
          darkLogoBackground: values.darkLogoBackground,
          qaUrl: values.qaUrl,
          inhibitionPassage: values.inhibitionPassage,
          defaultAnswer: values.defaultAnswer,
          noKnowledgeDefaultAnswer: values.noKnowledgeDefaultAnswer,
          description: values.description,
          interpolationString: values.interpolationString || "",
          customToolingInstructions: values.customToolingInstructions || "",
          dynamicModelSelectionInstructions:
            values.dynamicModelSelectionInstructions || "",
          goals: values.goals,
          disclaimer: values.disclaimer,
          quickQuestions: values.quickQuestions,
          allowFeedback: values.allowFeedback,
          promptBuffering: values.promptBuffering,
          agentsPool: values.agentsPool,
          agentDesktopCredentials: JSON.stringify(
            values.agentDesktopCredentials,
          ),
          agentDesktopBrowser: values.agentDesktopBrowser,
          allowExternalAPI: values.allowExternalAPI,
          isLongTermMemoryEnabled: values.isLongTermMemoryEnabled,
          agentLongTermMemory: values.agentLongTermMemory,
          languageDetectionConfidence: values.languageDetectionConfidence,
          maxPlanningSteps: values.maxPlanningSteps,
          maxBrowserSteps: values.maxBrowserSteps,
          plannerExecutionAttempts: values.plannerExecutionAttempts,
          planningInstructions: values.planningInstructions,
          moderationMessage: values.moderationMessage,
          analyticsInstructions: values.analyticsInstructions,
          analyticsCustomFields: values.analyticsCustomFields
            ? JSON.stringify(values.analyticsCustomFields)
            : null,
          alertIDs:
            values.alertIDs?.map((u) => u?.id)?.filter((u) => u) || [],
          ragInstructions: values.ragInstructions,
          minChunksBeforeCompleteness:
            values.minChunksBeforeCompleteness || 0.5,
          ragCompletenessThreshold: values.ragCompletenessThreshold || 0.75,
          agentDesktopVM: values.agentDesktopVM,
          agentDesktopPortRange: values.agentDesktopPortRange,
          plannerEmailOnApprovalRequest: values.plannerEmailOnApprovalRequest,
          plannerEmailOnFailedPlan: values.plannerEmailOnFailedPlan,
          plannerEmailOnCompletedPlan: values.plannerEmailOnCompletedPlan,
          plannerAutoAgentSpawn: values.plannerAutoAgentSpawn,
          plannerDeepThinking: values.plannerDeepThinking,
          plannerRetainsSpawnedAgents: values.plannerRetainsSpawnedAgents,
          reviewInstructions: values.reviewInstructions,
          maxTotalToolCalls: values.maxTotalToolCalls,
          maxConsecutiveSameTool: values.maxConsecutiveSameTool,
          maxConsecutiveFailures: values.maxConsecutiveFailures,
          maxSessionUoI: values.maxSessionUoI,
          maxSessionTurns: values.maxSessionTurns,
          maxSessionMessage: values.maxSessionMessage,
          plannerRequiresApprovalForPlanExecution:
            values.plannerRequiresApprovalForPlanExecution,
          preventWidgetUsage: values.preventWidgetUsage,
          color: values.color,
          allowIpRotationViaProxy: values.allowIpRotationViaProxy,
          allowProxyScraping: values.allowProxyScraping,
          placeholderInputMessage: values.placeholderInputMessage,
          placeholderMessageOnLoading: values.placeholderMessageOnLoading,
          messageOnLaunch: values.messageOnLaunch,
          charting: values.charting,
          hasTopicsContext: values.hasTopicsContext,
          pertinencePassage: values.pertinencePassage,
          advancedLanguageDetection: values.advancedLanguageDetection,
          splashTitle: values.splashTitle,
          showSplashWhenEmpty: values.showSplashWhenEmpty,
          contextRelevancyRatio: values.contextRelevancyRatio,
          reasoningBudget: values.reasoningBudget,
          reasoningMode: values.reasoningMode,
          reasoningEffort: values.reasoningEffort,
          keyWordsForKnowledgeBase: values.keyWordsForKnowledgeBase,
          subject: values.subject,
          mode: values.mode,
          maxTokens: values.maxTokens,
          showAgentName: values.showAgentName,
          showWidgetControls: values.showWidgetControls,
          showToolCallsDetailsInWidget: values.showToolCallsDetailsInWidget,
          hideReasoning: values.hideReasoning,
          maxTopics: values.maxTopics,
          topicEnhancer: values.topicEnhancer,
          isMultiLanguageEnabled: values.isMultiLanguageEnabled,
          restrictedAccess: values.restrictedAccess,
          forcedTheme: values.forcedTheme,
          restrictedAccessUsers: values.restrictedAccessUsers?.map(
            (item) => item.id,
          ),
          plannerApproverEmails: values.plannerApproverEmails?.map(
            (item) => item.id,
          ),
          allowedTopics: JSON.stringify(values.allowedTopics),
          staticDocs: JSON.stringify(values.staticDocs),
          ragSearchStrategy: values.ragSearchStrategy,
          functionContext: JSON.stringify(values.functionContext),
          topK: values.topK,
          summarisation: values.summarisation,
          maxHistory: values.maxHistory,
          compressor: values.compressor,
          docTopK: values.docTopK,
          llmProvider: values.llmProvider,
          llmBaseModel: values.llmBaseModel,
          regionalEndpoint: values.regionalEndpoint,
          agenticCodingModel: values.agenticCodingModel,
          functionCallingProvider: values.functionCallingProvider,
          functionCallingModel: values.functionCallingModel,
          adverserialProvider: values.adverserialProvider,
          adverserialModel: values.adverserialModel,
          visionModel: values.visionModel,
          browserModel: values.browserModel,
          voiceName: values.voiceName,
          sttModel: values.sttModel,
          llmVoiceModel: values.llmVoiceModel,
          ttsModel: values.ttsModel,
          defaultVoiceLanguage: values.defaultVoiceLanguage,
          voiceInstructions: values.voiceInstructions,
          voiceMinSilenceDuration: values.voiceMinSilenceDuration,
          voiceMinEndpointingDelay: values.voiceMinEndpointingDelay,
          voiceMinInterruptionDuration: values.voiceMinInterruptionDuration,
          enableVoiceIntermissions: values.enableVoiceIntermissions,
          enableJsonOutput: values.enableJsonOutput,
          jsonOutputStructure: values?.jsonOutputStructure
            ? JSON.stringify(values.jsonOutputStructure)
            : null,
          predictedPlanJson: values?.predictedPlanJson
            ? JSON.stringify(values.predictedPlanJson)
            : null,
          recencyImportance: values.recencyImportance,
          maxRecencyIncrease: values.maxRecencyIncrease,
          recencyTopKIncreaseFactor: values.recencyTopKIncreaseFactor,
          keywordsWeightInRetrieval: values.keywordsWeightInRetrieval || 0,
          extendedOutput: values.extendedOutput,
          workspaceID: getActiveWorkspace()?.id,
          hasImages: values.hasImages,
          hasCode: values.hasCode,
          allowImagesUpload: values.allowImagesUpload,
          allowAudiosUpload: values.allowAudiosUpload,
          allowDocsUpload: values.allowDocsUpload,
          allowVideosUpload: values.allowVideosUpload,
          allowIntrospection: values.allowIntrospection,
          allowInternetSearch: values.allowDeepInternetSearch,
          allowAskQuestion: values.allowAskQuestion ?? false,
          allowImagesGeneration: values.allowImagesGeneration,
          allowVideosGeneration: values.allowVideosGeneration,
          videoGenerationModel: values.videoGenerationModel,
          allow3dModelGeneration: values.allow3dModelGeneration,
          advancedImageGeneration: values.advancedImageGeneration,
          reRank: values.reRank,
          allowDeepInternetSearch: values.allowDeepInternetSearch,
          internetSearchModes: values.internetSearchModes?.map((u) => u?.value),
          internetSearchLocation: values.internetSearchLocation,
          bannedDomains: values.bannedDomains,
          maxUrlsForInternetSearch: values.maxUrlsForInternetSearch || 5,
          minDeepSearchPagesPercentage:
            values.minDeepSearchPagesPercentage || 0.5,
          hasNER: values.hasNER,
          showReferencesToInternalDocs: values.showReferencesToInternalDocs,
          showCitations: values.showCitations,
          agenticRAG: values.agenticRAG,
          useInterleavedReasoning: values.useInterleavedReasoning,
          interleavedPreset: values.interleavedPreset,
          llmSeed: values.llmSeed,
          hasFunctions: values.hasFunctions,
          agentFunctions: values.agentFunctions?.map((item) => item.id),
          agentType: values.agentType,
          plainTextOutput: values.plainTextOutput,
          generateExtendedReport: values.generateExtendedReport,
          minRetrievalScore: values.minRetrievalScore,
          hasModeration: values.hasModeration,
          customModerations: values?.customModerations
            ? JSON.stringify(values.customModerations)
            : null,
          agentFnParams: values?.agentFnParams
            ? JSON.stringify(values.agentFnParams)
            : null,
          showTimeForResponse: values.showTimeForResponse,
          dynamicPlaceholderMessageOnLoading:
            values.dynamicPlaceholderMessageOnLoading || true,
          customCodeExecutionEnvironments: values.customCodeExecutionEnvironments?.map(
            (item) => item.id,
          ),
          showDetectedLanguage: values.showDetectedLanguage,
          showRoutedModel: values.showRoutedModel,
          treeOfThoughts: values.treeOfThoughts,
          isGlobal: false,
          icon: values.icon,
          showCodeInExecution: values.showCodeInExecution,
          allowAudiosGeneration: values.allowAudiosGeneration,
          audiosGenerationTtsModel: values.audiosGenerationTtsModel || null,
          audiosGenerationVoiceName: values.audiosGenerationVoiceName || null,
          audiosGenerationSpeechMode: values.audiosGenerationSpeechMode || null,
          phoneNumberAsSender: values.phoneNumberAsSender,
          channelCallbackDelay: values.channelCallbackDelay,
          whatsappNumberAsSender: values.whatsappNumberAsSender,
          emailAsSender: values.emailAsSender,
          communicationServices: values.communicationServices?.map(
            (item) => item.id,
          ),
          sipPhoneNumber: values.sipPhoneNumber,
          enableInboundSip: values.enableInboundSip,
          enableOutboundSip: values.enableOutboundSip,
          sipDispatchRuleId: values.sipDispatchRuleId,
          agentVersion:
            semverHelper.normalizeVersion(values.agentVersion) || "0.1.0",
          agentCategoryType:
            values.agentCategoryType === "none" ||
            values.agentCategoryType === null
              ? null
              : values.agentCategoryType,
        };
        creationResult = await Agents.createAgent(newRole, user?.id);
        const newAgentsArray = [...state?.agents];
        newAgentsArray.push(creationResult?.data?.createAgent);
        dispatch("SET_AGENTS", newAgentsArray);
        loadAgents(getActiveWorkspace()?.id);
      }

      const sipChannel = values?.communicationServices?.find(
        (u) => u?.channel === "sip",
      );
      const savedAgentId = isEditMode
        ? values?.id
        : creationResult?.data?.createAgent?.id;

      if (
        !isEditMode &&
        values?.enableInboundSip &&
        values?.sipPhoneNumber &&
        sipChannel &&
        savedAgentId
      ) {
        try {
          const trunkResult = await sipAPI.createSipTrunk({
            workspace_id: getActiveWorkspace()?.id,
          });
          await sipAPI.createSipDispatchRule({
            workspace_id: getActiveWorkspace()?.id,
          });
          const originationUri =
            trunkResult?.data?.origination_uri ||
            `sip:${getActiveWorkspace()?.id}@sip.toothfairylab.link:5060`;
          AppToasty.show(
            `SIP configured! Configure Twilio Origination URI: ${originationUri}`,
            { type: "success" },
          );
        } catch (sipError) {
          AppToasty.show(
            `SIP setup failed: ${sipError?.message || "Unknown error"}`,
            {
              type: "warning",
            },
          );
        }
      }

      AppToasty.show(
        t(
          isEditMode
            ? "settings.qa.qaUpdateSuccessCaption"
            : "settings.qa.qaCreationSuccessCaption",
        ),
        {
          type: "success",
        },
      );
      formRef?.current?.resetForm?.({ values: formValuesSnapshot });
      setSaveVersion((v) => v + 1);
      try {
        console.log("[TF_DIRTY] save:resetForm", {
          snapshotKeys: Object.keys(formValuesSnapshot || {}).length,
        });
      } catch (e) {}
      if (!isEditMode && savedAgentId) {
        history.replace(
          `/workspaces/${getActiveWorkspace()?.id}/agents/${savedAgentId}`,
        );
      }
    } catch (error) {
      AppToasty.show(t("qaCreationErrorCaption"), {
        type: "danger",
      });
      console.log("error", error);
    } finally {
      setTimeout(() => {
        setIsAsyncProcessInProgress(false);
      }, 3000);
    }
  };

  const handleMemoryDeletion = async () => {
    if (selectedIndex != null && selectedIndex >= 0) {
      const _agentId = state?.agents?.[selectedIndex]?.id;
      try {
        await Agents.updateAgent(
          _agentId,
          [
            {
              property: "agentLongTermMemory",
              value: null,
            },
          ],
          user?.id,
        );
        AppToasty.show(t("toasts.memory.deleted"), {
          type: "success",
        });
      } catch (error) {
        AppToasty.show(t("toasts.memory.deleteFailed"), {
          type: "danger",
        });
      }
    }
  };

  const mode = builderCtx?.mode ?? initialMapValues?.mode;
  const hasCodeCtx = builderCtx?.hasCode ?? initialMapValues?.hasCode;
  const agenticRAGCtx = builderCtx?.agenticRAG ?? initialMapValues?.agenticRAG;
  const interleavedCtx =
    builderCtx?.useInterleavedReasoning ??
    initialMapValues?.useInterleavedReasoning;
  const restrictedAccessCtx =
    builderCtx?.restrictedAccess ?? initialMapValues?.restrictedAccess;

  const isSectionVisible = (id) => {
    switch (id) {
      case "profile":
      case "hosting":
      case "advanced":
        return true;
      case "instructions":
        return isChatBot && mode !== "planner";
      case "knowledge":
        return (
          !mode ||
          ["retriever", "speed", "accuracy", "coder", "voice"].includes(mode)
        );
      case "tools":
        return mode !== "coder";
      case "executionHooks":
        return ["coder", "retriever"].includes(mode) && !!hasCodeCtx;
      case "internet":
        return mode !== "coder" && mode !== "voice";
      case "memory":
        return mode !== "coder" && mode !== "voice";
      case "planning":
        return ["planner", "retriever"].includes(mode);
      case "notifications":
        return mode === "planner";
      case "capabilities":
        return !["coder", "computer"].includes(mode);
      case "desktop":
        return mode === "computer" || (agenticRAGCtx && mode === "retriever");
      case "channels":
        return mode !== "computer";
      case "access":
        return isChatBot && mode !== "voice" && mode !== "computer";
      case "sip":
        return mode === "voice";
      case "images":
      case "videos":
        return mode === "chatter" || (mode === "retriever" && !!interleavedCtx);
      case "multilingual":
        return !["planner", "computer", "voice"].includes(mode);
      case "moderation":
      case "analytics":
      case "feedback":
        return !["planner", "voice"].includes(mode);
      case "triggers":
      case "versions":
        return isEditMode;
      default:
        return true;
    }
  };

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("settings.qa.builderGroupSetup", "Set up"),
        items: [
          {
            id: "profile",
            title: t("settings.qa.builderSectionOverview", "Overview"),
            icon: AppAssets.icons.agent,
          },
          {
            id: "instructions",
            title: t("settings.qa.agentInstructions"),
            icon: AppAssets.icons.persona,
          },
          {
            id: "knowledge",
            title: t("settings.qa.knowledgeSettings"),
            icon: AppAssets.icons.notes,
          },
          {
            id: "tools",
            title: t("settings.qa.toolsAndHooksSettings", {
              defaultValue: "Tools & hooks settings",
            }),
            icon: AppAssets.icons.tools,
          },
          {
            id: "internet",
            title: t("settings.qa.internetSearch"),
            icon: AppAssets.icons.network,
          },
          {
            id: "memory",
            title: t("settings.qa.longTermMemory"),
            icon: AppAssets.icons.memory,
          },
        ],
      },
      {
        id: "intelligence",
        label: t("settings.qa.builderGroupIntelligence", "Intelligence"),
        items: [
          {
            id: "planning",
            title: t("settings.qa.planningTools"),
            icon: AppAssets.icons.agentsPlan,
          },
          {
            id: "capabilities",
            title: t("settings.qa.agentCapabilities"),
            icon: AppAssets.icons.capabililities,
          },
          {
            id: "triggers",
            title: t("settings.qa.builderSectionTriggers", "Triggers & jobs"),
            icon: AppAssets.icons.clock,
          },
          {
            id: "notifications",
            title: t("settings.qa.notifications"),
            icon: AppAssets.icons.notification,
          },
          {
            id: "desktop",
            title: t("settings.qa.desktopSetupAndCredentials"),
            icon: AppAssets.icons.credentials,
          },
        ],
      },
      {
        id: "engagement",
        label: t("settings.qa.builderGroupEngagement", "Engagement"),
        items: [
          {
            id: "images",
            title: t("settings.qa.imagesGeneration"),
            icon: AppAssets.icons.images,
          },
          {
            id: "videos",
            title: t("settings.qa.videos3dGeneration"),
            icon: AppAssets.icons.video,
          },
          {
            id: "multilingual",
            title: t("settings.qa.multilingual"),
            icon: AppAssets.icons.language,
          },
          {
            id: "moderation",
            title: t("settings.qa.moderationAndFeedback"),
            icon: AppAssets.icons.scan,
          },
          {
            id: "analytics",
            title: t("settings.qa.analytics"),
            icon: AppAssets.icons.charting,
          },
          {
            id: "feedback",
            title: t("settings.qa.agentFeedback"),
            icon: AppAssets.icons.teach,
          },
        ],
      },
      {
        id: "system",
        label: t("settings.qa.builderGroupSystem", "System"),
        items: [
          {
            id: "channels",
            title: t("settings.qa.agentChannels"),
            icon: AppAssets.icons.channels,
          },
          {
            id: "sip",
            title: t("settings.qa.sipPhoneConfiguration"),
            icon: AppAssets.icons.voiceUser,
          },
          {
            id: "hosting",
            title: t("settings.qa.hostingAndModels"),
            icon: AppAssets.icons.hosting,
          },
          {
            id: "advanced",
            title: t("settings.qa.advancedSettings"),
            icon: AppAssets.icons.experiment,
          },
          {
            id: "access",
            title: t("settings.qa.accessRestriction", {
              defaultValue: "Access restriction",
            }),
            icon: AppAssets.icons.lock,
          },
          {
            id: "evaluate",
            title: t("settings.qa.builderEvaluateTab", "Evaluation history"),
            icon: AppAssets.icons.charting,
          },
          {
            id: "versions",
            title: t("settings.qa.agentVersionsHistory"),
            icon: AppAssets.icons.history,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hasCodeCtx, agenticRAGCtx, interleavedCtx, isEditMode, isChatBot]);

  useEffect(() => {
    if (activeTab === "build" && !isSectionVisible(activeSection)) {
      setActiveSection("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hasCodeCtx, agenticRAGCtx, interleavedCtx]);

  // #hash routing: opening an agent at `/workspaces/:ws/agents/:agentId#<token>`
  // deep-links into the corresponding builder tab/section. Listens for changes
  // too, so programmatic hash updates (e.g. history.push with a hash) apply live.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_TABS.includes(raw)) {
        setActiveTab(raw);
        return;
      }
      if (HASH_ROUTED_SECTIONS.includes(raw) && isSectionVisible(raw)) {
        setActiveTab("build");
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, isEditMode, mode, hasCodeCtx, agenticRAGCtx, interleavedCtx]);

  const isWidgetTabVisible = isChatBot && restrictedAccessCtx !== true;

  const tabs = [
    {
      id: "build",
      label: t("settings.qa.builderBuildTab", "Assemble"),
      icon: AppAssets.icons.assemble,
    },
    {
      id: "run",
      label: t("settings.qa.builderRunTab", "Launch"),
      icon: AppAssets.icons.launch,
    },

    ...(isWidgetTabVisible
      ? [
          {
            id: "widget",
            label: t("settings.qa.builderWidgetTab", "Deploy"),
            icon: AppAssets.icons.deploy,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (activeTab === "widget" && !isWidgetTabVisible) setActiveTab("build");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWidgetTabVisible]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveTab("build");
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("settings.qa.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      try {
        console.log("[TF_DIRTY] nav:block", {
          to: location?.pathname,
          ctxIsDirty,
          formikDirty,
          effectiveIsDirty: isDirty,
        });
      } catch (e) {}
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/agents/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const builderActiveSection =
    activeTab === "build"
      ? activeSection
      : activeTab === "widget"
      ? "widget"
      : activeTab === "evaluate"
      ? "evaluate"
      : null;

  // Keep the URL hash in sync with the active tab/section so the current
  // builder view is always shareable (copy/paste a deep link back here).
  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeTab === "run" ? "run" : builderActiveSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderActiveSection, activeTab]);

  const runModeContextValue = useMemo(
    () => ({
      isHomeMode: true,
      agentId,
      routeBase: `agents/${agentId}`,
      filterByUser: false,
    }),
    [agentId],
  );

  const isFormReady =
    initialMapValues != null &&
    ((availableFunctionsLoaded && isConnectedToCloud()) ||
      !isConnectedToCloud()) &&
    (!isEditMode || (!!initialMapValues?.label && !isModelsLoading));

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [builderActiveSection]);

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={isAsyncProcessInProgress || !isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const renderTabButton = (tab) => {
    const isActive = activeTab === tab.id;
    return (
      <TouchableOpacity
        key={tab.id}
        onPress={() => setActiveTab(tab.id)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: 7,
          paddingHorizontal: 16,
          borderRadius: 20,
          backgroundColor: isActive
            ? currentTheme?.theme?.backgroundColor
            : currentTheme?.theme?.transparent,
          borderWidth: 1,
          borderColor: isActive
            ? currentTheme?.theme?.lightGrey
            : currentTheme?.theme?.transparent,
        }}
      >
        {tab.icon && (
          <AppIcon
            icon={tab.icon}
            size={13}
            color={
              isActive
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.darkGrey
            }
            style={{ marginRight: 7 }}
          />
        )}
        <AppText
          style={{ fontSize: 13 }}
          fontWeight={isActive ? "600" : "normal"}
          color={
            isActive
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.darkGrey
          }
        >
          {tab.label}
        </AppText>
      </TouchableOpacity>
    );
  };

  const renderSaveFirstHint = () => (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}
    >
      <AppIcon
        icon={AppAssets.icons.agent}
        size={26}
        color={currentTheme?.theme?.mediumGrey}
      />
      <AppText
        style={{ marginTop: 12, textAlign: "center" }}
        color={currentTheme?.theme?.mediumGrey}
      >
        {t(
          "settings.qa.builderSaveFirst",
          "Create the agent first to unlock this section",
        )}
      </AppText>
    </View>
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingAgents")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        {activeTab === "build" && (
          <AgentBuilderSidebar
            groups={sectionGroups}
            activeSection={activeSection}
            onSelect={setActiveSection}
            errorSections={errorSections}
          />
        )}
        <ScrollView
          ref={scrollRef}
          style={{
            flex: 1,
            minWidth: 0,
            display:
              activeTab === "run" || (!isEditMode && activeTab !== "build")
                ? "none"
                : "flex",
          }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <QaSettingsAI
            key={agentId}
            ref={formRef}
            setSelectedIndex={() => {}}
            availableFunctions={availableFunctions}
            customCodeExecutionEnvironments={customCodeExecutionEnvironments}
            availableFunctionsLoaded={availableFunctionsLoaded}
            customCodeExecutionEnvironmentsLoaded={
              customCodeExecutionEnvironmentsLoaded
            }
            agentsChannels={agentsChannels}
            displayedRoles={state?.agents}
            initialMapValues={initialMapValues}
            handleGenerationItemSave={handleGenerationItemSave}
            isAsyncProcessInProgress={isAsyncProcessInProgress}
            saveVersion={saveVersion}
            isEdit={isEditMode}
            isAIGenerated={isAIGenerated}
            selectedId={agentId}
            isChatBot={isChatBot}
            isBusinessAnalyst={isBusinessAnalyst}
            isContentCreator={isContentCreator}
            clearMemory={handleMemoryDeletion}
            selectedIndex={selectedIndex}
            setShowQaRoleModal={() => {}}
            isBuilderLayout
            builderActiveSection={builderActiveSection}
            onBuilderContextChange={setBuilderCtx}
          />
        </ScrollView>
        {isEditMode && (
          <View
            style={{
              flex: 1,
              minWidth: 0,
              display: activeTab === "run" ? "flex" : "none",
            }}
          >
            <HomeModeContext.Provider value={runModeContextValue}>
              <Chats builderMode />
            </HomeModeContext.Provider>
          </View>
        )}
        {activeTab === "run" && !isEditMode && renderSaveFirstHint()}
        {!isEditMode &&
          (activeTab === "evaluate" || activeTab === "widget") &&
          renderSaveFirstHint()}
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/roles_settings`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.agents")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.label || selectedAgent?.label || ""
            : history.location?.state?.label || t("settings.list.newAgent")}
        </AppText>
        <View
          style={{
            flex: 1,
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: currentTheme?.theme?.lightGrey,
              borderRadius: 24,
              padding: 4,
              gap: 2,
            }}
          >
            {tabs.map(renderTabButton)}
          </View>
        </View>
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("settings.qa.unsavedChangesSubCaption", {
          defaultValue:
            "This agent has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default AgentBuilderScreenContainer;
